import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "editing-the-photoshop-document"
    }}>{`Editing the Photoshop document`}</h1>
    <p>{`In this tutorial, you'll learn how to edit your document (more specifically, edit the layer names within your document).`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To follow this tutorial, you need to have a plugin development project ready to go. If you don't have a plugin
development project, yet, please first follow
the `}<a parentName="p" {...{
        "href": "../creating-your-first-plugin/index.md"
      }}>{`Creating your first plugin tutorial`}</a>{`.`}</p>
    <h2 {...{
      "id": "step-1-adjust-the-panel-ui"
    }}>{`Step 1: Adjust the panel UI`}</h2>
    <p>{`Since the plugin no longer needs to show the layer names in its UI, we can remove some elements from the `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` file.`}</p>
    <p>{`Remove the `}<inlineCode parentName="p">{`<sp-body id="layers" />`}</inlineCode>{` element, its associated styles, and rename the `}<em parentName="p">{`"Populate Layers"`}</em>{` button to `}<em parentName="p">{`"Rename Layers"`}</em>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<!DOCTYPE html>
<html>
  <head>
    <script src="index.js"></script>
  </head>
  <style>
    body {
      color: white;
      padding: 0 16px;
    }
  </style>
  <body>
    <sp-heading>Layers</sp-heading>
    <footer>
      <sp-button id="btnRename">Rename Layers</sp-button>
    </footer>
  </body>
</html>
`}</code></pre>
    <p>{`Note that we also changed the button's `}<inlineCode parentName="p">{`id`}</inlineCode>{` to `}<inlineCode parentName="p">{`"btnRename"`}</inlineCode>{`. With that (after re-loading, in case you don't have UDT's `}<em parentName="p">{`"Watch"`}</em>{` function enabled), your panel should look like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "251px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "148.20717131474103%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/a1942babd3e4a14bb5cfae6486e921d4/bbf50/panel.webp 251w"],
            "sizes": "(max-width: 251px) 100vw, 251px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/a1942babd3e4a14bb5cfae6486e921d4/26abe/panel.png 251w"],
            "sizes": "(max-width: 251px) 100vw, 251px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/a1942babd3e4a14bb5cfae6486e921d4/26abe/panel.png",
            "alt": "A Photoshop panel with a button \"Rename Layers\"",
            "title": "A Photoshop panel with a button \"Rename Layers\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-2-adjust-the-indexjs"
    }}>{`Step 2: Adjust the `}<inlineCode parentName="h2">{`index.js`}</inlineCode></h2>
    <p>{`Next, you need to adjust the `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` code to match the new functionality. You might have already tried (while tinkering around) to just use something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`layer.name = \`\${layer.name} (\${layer.opacity} %)\`;
`}</code></pre>
    <p>{`While that's already close, you will run into an error stating that "Such events are only allowed from inside the modal scope":`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "994px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "37.8125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/cb523/modal-execution-error.webp 320w", "/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/797b9/modal-execution-error.webp 640w", "/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/ce056/modal-execution-error.webp 994w"],
            "sizes": "(max-width: 994px) 100vw, 994px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/72799/modal-execution-error.png 320w", "/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/6af66/modal-execution-error.png 640w", "/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/75609/modal-execution-error.png 994w"],
            "sizes": "(max-width: 994px) 100vw, 994px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/1f41fd1df8758fb975c9221a2327216e/75609/modal-execution-error.png",
            "alt": "Chrome Developer Tools showing an error: \"Uncaught Error: Event: select may modify the state of Photoshop. Such events are only allowed from inside a modal scope\"",
            "title": "Chrome Developer Tools showing an error: \"Uncaught Error: Event: select may modify the state of Photoshop. Such events are only allowed from inside a modal scope\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To open a Chrome Developer Tools window for your plugin, select `}<em parentName="p">{`"Debug"`}</em>{` in the plugin actions menu in the UXP
Developer Tool.`}</p>
    <p>{`To get this to work, you need to wrap your code in a `}<inlineCode parentName="p">{`window.require('photoshop').core.executeAsModal(targetFunction, options)`}</inlineCode>{` call. This creates a context in which your plugin has exclusive access to manipulating the document and thus ensures that no changes can be made to the document while your code executes (which could result in strange errors). You need to use `}<inlineCode parentName="p">{`executeAsModal`}</inlineCode>{` for any operation that could edit the document. `}<a parentName="p" {...{
        "href": "/photoshop/uxp/src/pages/ps_reference/media/executeasmodal.md"
      }}>{`Learn more about modal execution`}</a>{`.`}</p>
    <p>{`Adjust your `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` code to look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`function renameLayerNames() {
  return window.require("photoshop").core.executeAsModal(
    () => {
      // now, we have write-access

      const app = window.require("photoshop").app;

      app.activeDocument.layers.forEach((layer) => {
        layer.name = \`\${layer.name} (\${layer.opacity} %)\`;
      });
    },
    {
      commandName: "Rename layers",
    }
  );
}

document
  .getElementById("btnRename")
  .addEventListener("click", renameLayerNames);
`}</code></pre>
    <p>{`When you reload your plugin using the UDT and press the new button, you can see that the layer names get adjusted accordingly:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "254px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "52.362204724409445%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/a1cd6f74f71ce29dc3092b96a5b362cb/14ddf/layer-names.webp 254w"],
            "sizes": "(max-width: 254px) 100vw, 254px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/a1cd6f74f71ce29dc3092b96a5b362cb/6a91e/layer-names.png 254w"],
            "sizes": "(max-width: 254px) 100vw, 254px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/a1cd6f74f71ce29dc3092b96a5b362cb/6a91e/layer-names.png",
            "alt": "Photoshop Layers panel with layer names \"Layer 1 (60 %)\" and \"Background (100 %)\"",
            "title": "Photoshop Layers panel with layer names \"Layer 1 (60 %)\" and \"Background (100 %)\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-3-solve-problem-with-consecutive-executions"
    }}>{`Step 3: Solve problem with consecutive executions`}</h2>
    <p>{`If you already have some development experience (or pressed the `}<em parentName="p">{`"Rename Layers"`}</em>{` buttons more than one time), you might already see a problem with our implementation. While the button works perfectly for the first time, every other time, it adds another percentage to the layer name:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "246px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "25.609756097560975%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/23f1556277ec5c03fed1101ac6af1b8d/18074/layer-name-problem.webp 246w"],
            "sizes": "(max-width: 246px) 100vw, 246px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/23f1556277ec5c03fed1101ac6af1b8d/92252/layer-name-problem.png 246w"],
            "sizes": "(max-width: 246px) 100vw, 246px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/23f1556277ec5c03fed1101ac6af1b8d/92252/layer-name-problem.png",
            "alt": "A layer with the name \"Layer 1 (60 %) (60 %) (60 %)\"",
            "title": "A layer with the name \"Layer 1 (60 %) (60 %) (60 %)\"",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`To fix this, change your layer loop to the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// [...]
app.activeDocument.layers.forEach((layer) => {
    // Regular Expression that searches for the layer's name
    // without a previously appended layer opacity.
    // Cf. https://regex101.com/r/iWF1Yw/1
    const regExp = /^(.*?)( \\(\\d+ %\\))?$/;
    const baseName = layer.name.match(regExp)[1];
    layer.name = \`\${baseName} (\${layer.opacity} %)\`;
});
// [...]
`}</code></pre>
    <p>{`This extracts the `}<inlineCode parentName="p">{`baseName`}</inlineCode>{`, i.e., the layer name without an optional percentage after it, and always uses this as the base for the new layer name.`}</p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`You now know how to use the Photoshop APIs to both read and write your document. As a rule of thumb, calling methods and setting properties that are declared as "Read-write" in the `}<a parentName="p" {...{
        "href": "/photoshop/uxp/src/pages/ps_reference/index.md"
      }}>{`Photoshop API Reference`}</a>{` requires a modal execution context.`}</p>
    <p><a parentName="p" {...{
        "href": "../writing-a-file/index.md"
      }}>{`In the next tutorial`}</a>{`, you will learn how to use the UXP APIs to interact with the file system and create a `}<inlineCode parentName="p">{`.tsv`}</inlineCode>{` file with a list of your layers.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      